/* eslint-disable @khan/frontend/require-stories -- we have stories for the Markdown component */
import {StyleSheet} from "aphrodite";

import SimpleMarkdown from "@khanacademy/simple-markdown";
import {addStyle} from "@khanacademy/wonder-blocks-core";

import type {MarkdownRule} from "../types.ts";

import {blockStyle} from "./shared-styles.ts";

const StyledOl = addStyle("ol");
const StyledUl = addStyle("ul");
const StyledLi = addStyle("li");

const BLOCK_END_R = /\n{2,}$/;
const LIST_ITEM_END_R = / *\n+$/;
const LIST_LOOKBEHIND_R = /(?:^|\n)( *)$/;

// Regex patterns for capturing list bullets:
// Ordered lists captures digits as list bullets
// Unordered lists captures *, +, - as list bullets
const UL_BULLET_PATTERN = "(?:[*+-])";
const OL_BULLET_PATTERN = "(?:\\d+\\.)";

// Regex patterns for capturing the prefix to list items:
// Ex. " • ", " - ", " + ", " 1. "
const OL_ITEM_PREFIX_PATTERN = "( *)(" + OL_BULLET_PATTERN + ") +";
const OL_ITEM_PREFIX_R = new RegExp("^" + OL_ITEM_PREFIX_PATTERN);

const UL_ITEM_PREFIX_PATTERN = "( *)(" + UL_BULLET_PATTERN + ") +";
const UL_ITEM_PREFIX_R = new RegExp("^" + UL_ITEM_PREFIX_PATTERN);

// Creates a regex for matching lines that start with a list item prefix and
// any following lines that don't start with the same list item prefix.
// For example:
//  * This is one list item
//    and this line this is part of the same item.
//
//    This is too but is a new paragraph in the same item
//
//  * This is a new list item
const getListItemRegex = ({ordered}: {ordered: boolean}): RegExp => {
    const ITEM_PREFEX_PATTERN = ordered
        ? OL_ITEM_PREFIX_PATTERN
        : UL_ITEM_PREFIX_PATTERN;
    const ITEM_BULLET_PATTERN = ordered ? OL_BULLET_PATTERN : UL_BULLET_PATTERN;
    return new RegExp(
        // Matches a line that starts with a list item prefix that is followed
        // by non-newline characters.
        `^${ITEM_PREFEX_PATTERN}[^\\n]*` +
            // Possibily followed by a new line that doesn't start with the
            // same list item prefix, and doesn't contain new line characters.
            // (\\1 is the first capture group in the prefix pattern)
            `(?:\\n(?!\\1${ITEM_BULLET_PATTERN} +)[^\\n]*)*` +
            // Ends with a new line or end of line.
            "(\\n|$)",
        "gm",
    );
};
const OL_ITEM_R = getListItemRegex({ordered: true});
const UL_ITEM_R = getListItemRegex({ordered: false});

// Creates a regex for matching lists and handles list items with paragraph
// content.
function getListRegex({ordered}: {ordered: boolean}) {
    const ITEM_PREFEX_PATTERN = ordered
        ? OL_ITEM_PREFIX_PATTERN
        : UL_ITEM_PREFIX_PATTERN;
    const ITEM_BULLET_PATTERN = ordered ? OL_BULLET_PATTERN : UL_BULLET_PATTERN;

    return new RegExp(
        // Matches text that starts with a list item prefix and is followed by
        // sequence of whitespace & non-whitespace characters.
        `^${ITEM_PREFEX_PATTERN}[\\s\\S]+?` +
            // Also followed by a paragraph that doesn't look like the start
            // of a new list.
            `(?:\\n{2,}(?! )(?!\\1${ITEM_BULLET_PATTERN} +)\\n*` +
            // OR the end of a list block.
            // the \\s*$ here is so that we can parse the inside of nested
            // lists, where our content might end before we receive two `\n`s
            "|\\s*\\n*$)",
    );
}

const ORDERED_LIST_R = getListRegex({ordered: true});
const UNORDERED_LIST_R = getListRegex({ordered: false});
const generateListRule = ({ordered}: {ordered: boolean}): MarkdownRule => {
    const LIST_R = ordered ? ORDERED_LIST_R : UNORDERED_LIST_R;
    const LIST_ITEM_R = ordered ? OL_ITEM_R : UL_ITEM_R;
    const LIST_ITEM_PREFIX_R = ordered ? OL_ITEM_PREFIX_R : UL_ITEM_PREFIX_R;

    return {
        match: (source, state, prevCapture) => {
            // We only want to break into a list if we are at the start of a
            // line. This is to avoid parsing "hi * there" with "* there"
            // becoming a part of a list.
            // You might wonder, "but that's inline, so of course it wouldn't
            // start a list?". You would be correct! Except that some of our
            // lists can be inline, because they might be inside another list,
            // in which case we can parse with inline scope, but need to allow
            // nested lists inside this inline scope.
            const isStartOfLine = LIST_LOOKBEHIND_R.exec(prevCapture);
            const isListBlock = state._list || !state.inline;

            if (isStartOfLine && isListBlock) {
                source = isStartOfLine[1] + source;
                return LIST_R.exec(source);
            }
            return null;
        },
        order: SimpleMarkdown.defaultRules.list.order,
        parse: (capture, parse, state) => {
            const bullet = capture[2];
            const start = ordered ? +bullet : undefined;
            const items = capture[0]
                // recognize the end of a paragraph block inside a list item:
                // two or more newlines at the end of the item
                .replace(BLOCK_END_R, "\n")
                .match(LIST_ITEM_R);

            let lastItemWasAParagraph = false;
            // @ts-expect-error - TS2531 - Object is possibly 'null'.
            const itemContent = items.map(function (item, i) {
                // If there contains a nested list, we need to see how far
                // indented it is so that we can "unindent" subsequent
                // lines by the same amount.
                const nestedListPrefix = new RegExp(
                    `\\n( *)(${OL_BULLET_PATTERN}|${UL_BULLET_PATTERN}) +`,
                );
                const indent = nestedListPrefix.exec(item)?.[1]?.length ?? 0;
                const indentRegex =
                    indent >= 1 ? new RegExp("^ {1," + indent + "}", "gm") : "";

                // Before processing the item, we need a couple things
                const content = item
                    .replace(indentRegex, "") // unindent lines
                    .replace(LIST_ITEM_PREFIX_R, ""); // remove the bullet:

                // Handling "loose" lists, like:
                //
                //  * this is wrapped in a paragraph
                //
                //  * as is this
                //
                //  * as is this
                // @ts-expect-error - TS2531 - Object is possibly 'null'.
                const isLastItem = i === items.length - 1;
                const containsBlocks = content.indexOf("\n\n") !== -1;

                // Any element in a list is a block if it contains multiple
                // newlines. The last element in the list can also be a block
                // if the previous item in the list was a block (this is
                // because non-last items in the list can end with \n\n, but
                // the last item can't, so we just "inherit" this property
                // from our previous element).
                const thisItemIsAParagraph =
                    containsBlocks || (isLastItem && lastItemWasAParagraph);
                lastItemWasAParagraph = thisItemIsAParagraph;

                // backup our state for restoration afterwards. We're going to
                // want to set state._list to true, and state.inline depending
                // on our list's looseness.
                const oldStateInline = state.inline;
                const oldStateList = state._list;
                state._list = true;

                // Parse inline if we're in a tight list, or block if we're in
                // a loose list.
                let adjustedContent;
                if (thisItemIsAParagraph) {
                    state.inline = false;
                    adjustedContent = content.replace(LIST_ITEM_END_R, "\n\n");
                } else {
                    state.inline = true;
                    adjustedContent = content.replace(LIST_ITEM_END_R, "");
                }

                const result = parse(adjustedContent, state);

                // Restore our state before returning
                state.inline = oldStateInline;
                state._list = oldStateList;

                return result;
            });

            return {
                items: itemContent,
                ordered: ordered,
                start: start,
            };
        },
        react: (node, output, state) => {
            const List = node.ordered ? StyledOl : StyledUl;
            return (
                <List
                    key={state.key}
                    start={node.start}
                    style={[
                        styles.list,
                        node.ordered
                            ? styles.orderedList
                            : styles.unorderedList,
                    ]}
                >
                    {node.items?.map((item, i) => (
                        <StyledLi style={styles.listItem} key={i}>
                            {output(item, state)}
                        </StyledLi>
                    ))}
                </List>
            );
        },
    };
};

export const orderedListRule: Readonly<MarkdownRule> = generateListRule({
    ordered: true,
});
export const unorderedListRule: Readonly<MarkdownRule> = generateListRule({
    ordered: false,
});

const styles = StyleSheet.create({
    list: {
        listStyle: "initial",
        paddingInlineStart: "2em",
        ...blockStyle,
    },
    listItem: {
        whiteSpace: "pre-wrap",
        marginBlockStart: "0.5em",
        marginBlockEnd: "0.5em",
    },
    orderedList: {
        listStyleType: "decimal",
    },
    unorderedList: {
        listStyleType: "disc",
    },
});
